/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase A R4 A3 / R9 F — MirroredWaveform → SCROLLING dual-deck view.
 *
 * VDJ / Serato-style scrolling waveform pair. Each deck shows a moving
 * time window (default ~24s, with the playhead at the 33% mark). The
 * waveform scrolls right-to-left under a stationary playhead, the
 * future of the track approaches from the right, and 4 STATIONARY
 * downbeat reference bars are painted at fixed x positions ahead of
 * the playhead so the DJ can see when an incoming downbeat lines up
 * with them on the other deck for clean beatmatching.
 *
 * Layout:
 *   - Deck A scrolls on the TOP half (waveform grows upward).
 *   - Deck B scrolls on the BOTTOM half (waveform grows downward).
 *   - Same time axis on both halves (each deck shows its OWN time
 *     window — they are not chained together because the tracks have
 *     different durations).
 *
 * Implementation:
 *   - One canvas, ~60fps RAF redraw. Cheap because each frame draws
 *     only the visible columns (~120 max), not the whole track.
 *   - Beat grid + measure (downbeat) accents are computed in TRACK
 *     time and projected onto the scrolling canvas → they scroll with
 *     the waveform.
 *   - 4 reference downbeat bars are painted at fixed screen x's
 *     (independent of scroll) so the DJ can see when the OUTGOING
 *     deck's audible downbeat coincides with one of those bars on the
 *     INCOMING deck.
 *
 * The `data-testid` is kept as `mirrored-waveform` for regression
 * compatibility, plus `dj-scrolling-waveforms-wrap` is added.
 */
import { useEffect, useRef, useState } from "react";

const VISIBLE_WINDOW_SEC = 24;  // 8s past + 16s future
const PLAYHEAD_FRAC      = 1 / 3;

function getBandColors(host) {
  const cs = host ? getComputedStyle(host) : null;
  const lo  = cs?.getPropertyValue("--sr-band-low").trim()  || "#a855f7";
  const md  = cs?.getPropertyValue("--sr-band-mid").trim()  || "#34d399";
  const hi  = cs?.getPropertyValue("--sr-band-high").trim() || "#ffffff";
  return { lo, md, hi };
}

function useBands(engineRef, deckLabel, deckState) {
  const [bands, setBands] = useState(null);
  const [cols, setCols]   = useState(0);
  useEffect(() => {
    const eng = engineRef.current;
    const deck = eng?.["deck" + deckLabel];
    if (!deck) { setBands(null); setCols(0); return; }
    if (deck._bands && deck._bandsColumns) {
      setBands(deck._bands);
      setCols(deck._bandsColumns);
    } else {
      setBands(null);
      setCols(0);
    }
    // eslint-disable-next-line
  }, [deckLabel, deckState?.title, deckState?.dur, deckState?.bpm, deckState?.hasBands]);
  return { bands, cols };
}

function PairLabel({ label, bpm, key: keyName, accent }) {
  return (
    <div className="absolute top-1 left-1 z-10 px-1.5 py-0.5 rounded text-[10px] font-bold flex items-center gap-1.5 bg-black/55 border border-white/10"
         data-testid={`mirrored-label-${label}`} style={{ color: accent }}>
      <span>{label}</span>
      {bpm > 0 && <span className="text-white/85">{bpm.toFixed(2)} BPM</span>}
      {keyName && <span className="text-white/85">{keyName}</span>}
    </div>
  );
}

export default function MirroredWaveform({
  engineRef,
  deckA, deckB,
  height = 130,
  onSeek = null,
  testid = "mirrored-waveform",
}) {
  const wrapRef    = useRef(null);
  const canvasRef  = useRef(null);
  const rafRef     = useRef(0);

  const eng = engineRef.current;
  const A   = useBands(engineRef, "A", deckA);
  const B   = useBands(engineRef, "B", deckB);
  const bandsRef = useRef({ A: null, B: null });
  useEffect(() => { bandsRef.current = { A, B }; }, [A.bands, A.cols, B.bands, B.cols]);

  // Scrolling draw loop. One canvas; per-frame projection of band data
  // onto the visible window. Cheap (~120 columns × 2 decks × 3 bands).
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const dpr = window.devicePixelRatio || 1;

    const drawDeck = (g, e, deck, isB, W, H, bandsState) => {
      const halfH = H / 2;
      const y0   = isB ? halfH : 0;
      const yEnd = isB ? H : halfH;
      const pos = deck?.currentTime || 0;
      const dur = deck?.duration || 0;
      const bpm = deck?.bpm || 0;
      const firstBeat = deck?.firstBeatTime || 0;
      const { lo, md, hi } = getBandColors(wrapRef.current);
      const accent = lo;

      const pxPerSec = W / VISIBLE_WINDOW_SEC;
      const playheadX = W * PLAYHEAD_FRAC;
      const windowStart = pos - VISIBLE_WINDOW_SEC * PLAYHEAD_FRAC;
      const windowEnd   = pos + VISIBLE_WINDOW_SEC * (1 - PLAYHEAD_FRAC);

      // Bands at every track-time projected to screen x.
      if (bandsState?.bands && bandsState?.cols && dur > 0) {
        const cols = bandsState.cols;
        const secPerCol = dur / cols;
        // Determine which columns are inside the visible window.
        const cStart = Math.max(0, Math.floor(windowStart / secPerCol));
        const cEnd   = Math.min(cols - 1, Math.ceil(windowEnd / secPerCol));
        const barW = Math.max(1, pxPerSec * secPerCol);
        for (let c = cStart; c <= cEnd; c++) {
          const tCol = c * secPerCol;
          const x = playheadX + (tCol - pos) * pxPerSec;
          if (x < -barW || x > W) continue;
          const L  = bandsState.bands[c * 3 + 0];
          const M  = bandsState.bands[c * 3 + 1];
          const HI = bandsState.bands[c * 3 + 2];
          const lH = L  * halfH * 0.9;
          const mH = M  * halfH * 0.9;
          const hH = HI * halfH * 0.9;
          if (isB) {
            let y = halfH;
            g.fillStyle = lo; g.fillRect(x, y, barW, lH);
            g.fillStyle = md; g.fillRect(x, y + lH, barW, mH);
            g.fillStyle = hi; g.fillRect(x, y + lH + mH, barW, hH);
          } else {
            let y = halfH;
            g.fillStyle = lo; g.fillRect(x, y - lH, barW, lH);
            g.fillStyle = md; g.fillRect(x, y - lH - mH, barW, mH);
            g.fillStyle = hi; g.fillRect(x, y - lH - mH - hH, barW, hH);
          }
        }
      }

      // Beat grid + measure accents (scroll with the waveform).
      if (bpm > 0 && dur > 0) {
        const beatSec = 60 / bpm;
        // Determine first beat that's >= windowStart.
        const offset = (firstBeat || 0) % beatSec;
        const firstBeatInWindow = Math.ceil((windowStart - offset) / beatSec) * beatSec + offset;
        const downbeatOffset = Math.floor((firstBeat || 0) / beatSec) % 4;
        let beatIdx = Math.round((firstBeatInWindow - offset) / beatSec);
        for (let t = firstBeatInWindow; t < windowEnd && t < dur; t += beatSec, beatIdx++) {
          const x = playheadX + (t - pos) * pxPerSec;
          if (x < 0 || x > W) continue;
          const isDown = ((beatIdx + (4 - downbeatOffset)) % 4) === 0;
          if (isDown) {
            g.strokeStyle = accent + "AA";
            g.lineWidth = 1.4;
            g.beginPath(); g.moveTo(x, y0); g.lineTo(x, yEnd); g.stroke();
          } else {
            g.strokeStyle = "rgba(255,255,255,0.12)";
            g.lineWidth = 0.5;
            g.beginPath(); g.moveTo(x, y0); g.lineTo(x, yEnd); g.stroke();
          }
        }
      }

      // STATIONARY reference downbeat bars (4 ahead of the playhead).
      // These are fixed at screen positions; the DJ uses them as the
      // mental "phase reference" — a beatmatched incoming track will
      // have its downbeats visibly drift in lockstep with the
      // outgoing's downbeats and align with these bars.
      if (bpm > 0) {
        const barSec = (60 / bpm) * 4;
        for (let i = 1; i <= 4; i++) {
          const x = playheadX + barSec * pxPerSec * i;
          if (x < 0 || x > W) continue;
          g.strokeStyle = "rgba(255,255,255,0.35)";
          g.lineWidth = 0.75;
          g.setLineDash([4, 3]);
          g.beginPath(); g.moveTo(x, y0); g.lineTo(x, yEnd); g.stroke();
          g.setLineDash([]);
        }
      }

      // Stationary playhead — full-height accent line at x = W * 1/3.
      g.strokeStyle = "rgba(255,255,255,0.95)";
      g.lineWidth = 1.5;
      g.beginPath(); g.moveTo(playheadX, y0); g.lineTo(playheadX, yEnd); g.stroke();
    };

    const draw = () => {
      const W = cvs.clientWidth, H = cvs.clientHeight;
      if (cvs.width !== Math.max(1, W * dpr) || cvs.height !== Math.max(1, H * dpr)) {
        cvs.width = Math.max(1, W * dpr); cvs.height = Math.max(1, H * dpr);
      }
      const g = cvs.getContext("2d");
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.clearRect(0, 0, W, H);
      // Horizontal center separator
      g.fillStyle = "rgba(255,255,255,0.10)";
      g.fillRect(0, H / 2 - 0.5, W, 1);

      const e = engineRef.current;
      if (e) {
        drawDeck(g, e, e.deckA, false, W, H, bandsRef.current.A);
        drawDeck(g, e, e.deckB, true,  W, H, bandsRef.current.B);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // Phase A R9 F — click-to-seek now interprets x relative to the
  // SCROLLING window, not the static-track layout: clicking ahead of
  // the stationary playhead jumps the deck forward by that many
  // seconds, behind jumps it back. The half (top vs bottom) selects
  // which deck the seek applies to (same as before).
  const onClickCanvas = (e) => {
    if (!onSeek) return;
    const r = e.currentTarget.getBoundingClientRect();
    const xPx = e.clientX - r.left;
    const W = r.width;
    const which = (e.clientY - r.top) > r.height / 2 ? "B" : "A";
    const deck = which === "A" ? eng?.deckA : eng?.deckB;
    if (!deck?.duration) return;
    const playheadX = W * PLAYHEAD_FRAC;
    const pxPerSec = W / VISIBLE_WINDOW_SEC;
    const deltaSec = (xPx - playheadX) / pxPerSec;
    const targetT = Math.max(0, Math.min(deck.duration, (deck.currentTime || 0) + deltaSec));
    const frac = targetT / deck.duration;
    onSeek(which, frac);
  };

  const accentA = "#a855f7";
  const accentB = "#34d399";

  return (
    <div ref={wrapRef}
         className="relative w-full select-none rounded-md bg-black/40 border border-white/10 overflow-hidden"
         style={{ height }}
         onClick={onClickCanvas}
         data-testid={testid}
         data-sr-scrolling-wave>
      <PairLabel label="A" bpm={deckA?.bpm} keyName={eng?.deckA?.detectedKey} accent={accentA} />
      <div className="absolute bottom-1 left-1 z-10 px-1.5 py-0.5 rounded text-[10px] font-bold flex items-center gap-1.5 bg-black/55 border border-white/10"
           data-testid="mirrored-label-B" style={{ color: accentB }}>
        <span>B</span>
        {(deckB?.bpm || 0) > 0 && <span className="text-white/85">{deckB.bpm.toFixed(2)} BPM</span>}
        {eng?.deckB?.detectedKey && <span className="text-white/85">{eng.deckB.detectedKey}</span>}
      </div>
      <div className="absolute top-1 right-1 z-10 px-1.5 py-0.5 rounded text-[9px] font-mono bg-black/55 border border-white/10 text-white/70"
           data-testid="mirrored-window-hint">
        scrolling · {VISIBLE_WINDOW_SEC}s · ref ⫶ next 4 measures
      </div>
      <canvas ref={canvasRef}
              className="absolute inset-0 w-full h-full block"
              data-testid="dj-scrolling-waveforms-wrap" />
    </div>
  );
}
